import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef
} from 'react';

const CACHE_VERSION = 'v2';
const DEFAULT_MAX_ENTRIES = 20;

export interface CachedSearchResult<T> {
  searchQuery: string;
  locations: string[];
  results: T[];
  timestamp: number;
}

export interface SearchCacheKey {
  tripId: string;
  destinationId: string;
  searchQuery: string;
  /** Effective location string at time of search, or '' if location is disabled */
  locationString?: string;
  /** Active profile _id, or undefined / 'none' if personalisation is off */
  profileId?: string;
}

export interface SearchCacheContextValue<T> {
  getCachedSearch: (key: SearchCacheKey) => CachedSearchResult<T> | null;
  setCachedSearch: (
    key: SearchCacheKey,
    locations: string[],
    results: T[]
  ) => void;
  clearCachedSearch: (key: SearchCacheKey) => void;
  clearTripCache: (tripId: string) => void;
  clearAllCache: () => void;
  getCacheStats: () => { size: number; maxSize: number };
}

type SearchType =
  | 'ai_summaries'
  | 'youtube_videos'
  | 'viator_tours'
  | 'web_results';

interface CacheEntry<T> {
  key: string;
  data: CachedSearchResult<T>;
  lastAccessed: number;
}

/**
 * Generic factory function to create typed search cache contexts
 * @param searchType - The type of search results being cached
 * @param maxEntries - Maximum number of cache entries (default: 20)
 * @returns Provider component and useCache hook
 */
export function createSearchCacheContext<T>(
  searchType: SearchType,
  maxEntries: number = DEFAULT_MAX_ENTRIES
) {
  const STORAGE_KEY = `search_cache_${searchType}_${CACHE_VERSION}`;

  const CacheContext = createContext<SearchCacheContextValue<T> | undefined>(
    undefined
  );

  /**
   * Generate a normalized cache key from search parameters
   */
  function generateCacheKey(key: SearchCacheKey): string {
    const { tripId, destinationId, searchQuery, locationString, profileId } =
      key;
    const normalizedQuery = searchQuery.trim().toLowerCase();
    const normalizedLocation = (locationString ?? '').trim().toLowerCase();
    const effectiveProfile = profileId || 'none';
    return `trip_${tripId}_dest_${destinationId}_loc_${normalizedLocation}_profile_${effectiveProfile}_query_${normalizedQuery}`;
  }

  /**
   * Check if sessionStorage is available
   */
  function isSessionStorageAvailable(): boolean {
    try {
      const test = '__storage_test__';
      sessionStorage.setItem(test, test);
      sessionStorage.removeItem(test);
      return true;
    } catch (e) {
      console.warn(
        `[${searchType}] SessionStorage not available, using in-memory cache only`
      );
      return false;
    }
  }

  /**
   * Load cache from sessionStorage
   */
  function loadCacheFromStorage(): Map<string, CacheEntry<T>> {
    if (!isSessionStorageAvailable()) {
      return new Map();
    }

    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return new Map();
      }

      const parsed = JSON.parse(stored);
      const cacheMap = new Map<string, CacheEntry<T>>();

      Object.entries(parsed).forEach(([key, value]: [string, any]) => {
        cacheMap.set(key, {
          key,
          data: value,
          lastAccessed: value.timestamp || Date.now()
        });
      });

      return cacheMap;
    } catch (error) {
      console.error(
        `[${searchType}] Failed to load cache from sessionStorage:`,
        error
      );
      // Clear corrupted cache
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // Ignore
      }
      return new Map();
    }
  }

  /**
   * Save cache to sessionStorage
   */
  function saveCacheToStorage(cache: Map<string, CacheEntry<T>>): void {
    if (!isSessionStorageAvailable()) {
      return;
    }

    try {
      const cacheObject: Record<string, CachedSearchResult<T>> = {};
      cache.forEach((entry, key) => {
        cacheObject[key] = entry.data;
      });

      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cacheObject));
    } catch (error: any) {
      console.error(
        `[${searchType}] Failed to save cache to sessionStorage:`,
        error
      );

      // Handle quota exceeded
      if (error.name === 'QuotaExceededError') {
        console.warn(
          `[${searchType}] Storage quota exceeded, clearing oldest entries`
        );
        // Keep only the most recent half of entries
        const sortedEntries = Array.from(cache.entries()).sort(
          (a, b) => b[1].lastAccessed - a[1].lastAccessed
        );
        const newCache = new Map(
          sortedEntries.slice(0, Math.floor(maxEntries / 2))
        );
        try {
          const cacheObject: Record<string, CachedSearchResult<T>> = {};
          newCache.forEach((entry, key) => {
            cacheObject[key] = entry.data;
          });
          sessionStorage.setItem(STORAGE_KEY, JSON.stringify(cacheObject));
        } catch (retryError) {
          console.error(
            `[${searchType}] Failed to save reduced cache:`,
            retryError
          );
        }
      }
    }
  }

  /**
   * Evict least recently used entries when cache is full
   */
  function evictLRU(cache: Map<string, CacheEntry<T>>): void {
    if (cache.size <= maxEntries) {
      return;
    }

    // Sort by last accessed time (ascending)
    const sortedEntries = Array.from(cache.entries()).sort(
      (a, b) => a[1].lastAccessed - b[1].lastAccessed
    );

    // Remove oldest entries
    const entriesToRemove = cache.size - maxEntries;
    for (let i = 0; i < entriesToRemove; i++) {
      const [key] = sortedEntries[i];
      cache.delete(key);
    }
  }

  const Provider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const [cache, setCache] = useState<Map<string, CacheEntry<T>>>(() =>
      loadCacheFromStorage()
    );
    const cacheRef = useRef<Map<string, CacheEntry<T>>>(cache);
    const saveTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Debounced save to sessionStorage
    const debouncedSave = useCallback(
      (updatedCache: Map<string, CacheEntry<T>>) => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }

        saveTimeoutRef.current = setTimeout(() => {
          saveCacheToStorage(updatedCache);
        }, 500); // 500ms debounce
      },
      []
    );

    // Save cache to sessionStorage whenever it changes
    useEffect(() => {
      debouncedSave(cache);
    }, [cache, debouncedSave]);

    // Keep a current cache reference for stable callbacks.
    useEffect(() => {
      cacheRef.current = cache;
    }, [cache]);

    // Cleanup on unmount
    useEffect(() => {
      return () => {
        if (saveTimeoutRef.current) {
          clearTimeout(saveTimeoutRef.current);
        }
      };
    }, []);

    const getCachedSearch = useCallback(
      (key: SearchCacheKey): CachedSearchResult<T> | null => {
        const cacheKey = generateCacheKey(key);
        const entry = cacheRef.current.get(cacheKey);

        if (!entry) {
          return null;
        }

        // Update in-memory access time without triggering a re-render.
        cacheRef.current.set(cacheKey, { ...entry, lastAccessed: Date.now() });

        return entry.data;
      },
      []
    );

    const setCachedSearch = useCallback(
      (key: SearchCacheKey, locations: string[], results: T[]): void => {
        const cacheKey = generateCacheKey(key);
        const newEntry: CacheEntry<T> = {
          key: cacheKey,
          data: {
            searchQuery: key.searchQuery.trim(),
            locations,
            results,
            timestamp: Date.now()
          },
          lastAccessed: Date.now()
        };

        setCache((prev) => {
          const newCache = new Map(prev);
          newCache.set(cacheKey, newEntry);

          // Evict LRU entries if needed
          evictLRU(newCache);

          return newCache;
        });
      },
      []
    );

    const clearCachedSearch = useCallback((key: SearchCacheKey): void => {
      const cacheKey = generateCacheKey(key);
      setCache((prev) => {
        const newCache = new Map(prev);
        const deleted = newCache.delete(cacheKey);

        if (deleted) {
          console.log(`[${searchType}] Cleared cache entry: ${cacheKey}`);
          return newCache;
        }

        return prev;
      });
    }, []);

    const clearTripCache = useCallback((tripId: string): void => {
      const prefix = `trip_${tripId}_`;
      setCache((prev) => {
        const newCache = new Map(prev);
        let clearedCount = 0;

        Array.from(newCache.keys()).forEach((key) => {
          if (key.startsWith(prefix)) {
            newCache.delete(key);
            clearedCount++;
          }
        });

        if (clearedCount > 0) {
          console.log(
            `[${searchType}] Cleared ${clearedCount} cache entries for trip: ${tripId}`
          );
          return newCache;
        }

        return prev;
      });
    }, []);

    const clearAllCache = useCallback((): void => {
      setCache(new Map());
      try {
        sessionStorage.removeItem(STORAGE_KEY);
      } catch (e) {
        // Ignore
      }
      console.log(`[${searchType}] Cleared all cache`);
    }, []);

    const getCacheStats = useCallback((): { size: number; maxSize: number } => {
      return {
        size: cacheRef.current.size,
        maxSize: maxEntries
      };
    }, []);

    const value: SearchCacheContextValue<T> = {
      getCachedSearch,
      setCachedSearch,
      clearCachedSearch,
      clearTripCache,
      clearAllCache,
      getCacheStats
    };

    return (
      <CacheContext.Provider value={value}>{children}</CacheContext.Provider>
    );
  };

  const useCache = (): SearchCacheContextValue<T> => {
    const context = useContext(CacheContext);
    if (!context) {
      throw new Error(
        `useCache must be used within ${searchType} SearchCacheProvider`
      );
    }
    return context;
  };

  return { Provider, useCache };
}
